/**
 * FaceGallery - Horizontal scrolling gallery of captured face images
 * Displays thumbnails with image count. Lightbox/cropper rendering is handled at page level.
 */

import { ImageIcon } from 'lucide-react';
import { FaceImageThumbnail } from './FaceImageThumbnail';
import { cn } from '@/lib/utils';
import type { FaceImage } from '@/stores/diceRegistryStore';

/**
 * Props for FaceGallery component
 */
interface FaceGalleryProps {
  /** Face number being displayed */
  faceNumber: number;
  /** Array of captured images for this face */
  images: FaceImage[];
  /** Callback when an image is deleted */
  onDeleteImage: (imageId: string) => void;
  /** Callback when a thumbnail is clicked — parent handles lightbox */
  onImageClick?: (image: FaceImage) => void;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Gallery component showing captured images for a dice face.
 * Features horizontal scrolling and image count.
 * Lightbox and cropper are rendered at page level to avoid drawer stacking context issues.
 */
export function FaceGallery({
  faceNumber,
  images,
  onDeleteImage: _onDeleteImage,
  onImageClick,
  className
}: FaceGalleryProps) {
  const imageCount = images.length;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header with image count */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground flex items-center gap-1">
          <ImageIcon className="w-4 h-4" />
          Face {faceNumber}
        </span>
        <span
          className={cn(
            'font-medium',
            imageCount > 0 ? 'text-green-500' : 'text-muted-foreground'
          )}
          role="status"
          aria-live="polite"
        >
          {imageCount} {imageCount === 1 ? 'image' : 'images'} captured
        </span>
      </div>

      {/* Gallery container */}
      {imageCount > 0 ? (
        <div
          className={cn(
            'flex gap-2 overflow-x-auto py-2 px-1',
            'scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent',
            'snap-x snap-mandatory'
          )}
          role="group"
          aria-label={`Gallery for face ${faceNumber}`}
        >
          {images.map((image) => (
            <div key={image.id} className="snap-start">
              <FaceImageThumbnail
                image={image}
                onClick={() => onImageClick?.(image)}
              />
            </div>
          ))}
        </div>
      ) : (
        /* Empty state */
        <div
          className={cn(
            'flex items-center justify-center h-20',
            'border-2 border-dashed border-border rounded-md',
            'text-muted-foreground text-sm'
          )}
        >
          No images captured yet
        </div>
      )}
    </div>
  );
}

export default FaceGallery;
